import { MessageGroup } from "@somethings-ui/web";
import { Loader2, Maximize2 } from "lucide-react";
import { useEffect, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { convertHeicBlobToJpeg, isHeicUrl } from "@/lib/utils/heic-converter";
import { cn } from "@/lib/utils";
import type { AttentionLightboxMedia } from "./attention-lightbox";
import type { AttentionImageMedia } from "./attention-message";
import { resolveMediaBoxStyle } from "./attention-media-size";
import type { AttentionBubbleItem } from "./attention-view";
import { AttentionVoiceNote } from "./attention-voice-note";

export function AttentionMedia({
  item,
  onMediaSettled,
  onOpenMedia,
}: {
  item: Extract<AttentionBubbleItem, { type: "media" }>;
  onMediaSettled?: () => void;
  onOpenMedia?: (media: AttentionLightboxMedia) => void;
}) {
  const { caption } = item;

  const content = (
    <MediaContent
      item={item}
      onMediaSettled={onMediaSettled}
      onOpenMedia={onOpenMedia}
    />
  );

  if (caption === null || caption.length === 0) {
    return content;
  }

  return (
    <div className="flex flex-col gap-1">
      {content}
      <span className="px-1 font-body text-body-3 leading-4">{caption}</span>
    </div>
  );
}

function MediaContent({
  item,
  onMediaSettled,
  onOpenMedia,
}: {
  item: Extract<AttentionBubbleItem, { type: "media" }>;
  onMediaSettled?: () => void;
  onOpenMedia?: (media: AttentionLightboxMedia) => void;
}) {
  const { media } = item;
  switch (media.mediaType) {
    case "image":
      return (
        <AttentionImage
          media={media}
          onMediaSettled={onMediaSettled}
          onOpenMedia={onOpenMedia}
        />
      );
    case "video": {
      if (media.url === null) {
        return <MediaPlaceholder label="Video unavailable" />;
      }
      const videoUrl = media.url;
      const { posterUrl } = media;
      return (
        <InlineVideo
          url={videoUrl}
          posterUrl={posterUrl}
          onMediaSettled={onMediaSettled}
          onOpen={
            onOpenMedia
              ? () => onOpenMedia({ kind: "video", url: videoUrl, posterUrl })
              : undefined
          }
        />
      );
    }
    case "audio":
      return (
        <AttentionVoiceNote
          src={media.url ?? undefined}
          sdkId={media.sdkId}
          reqId={media.reqId}
        />
      );
    default: {
      const exhaustive: never = media;
      return exhaustive;
    }
  }
}

type ResolvedImageStatus = "ready" | "converting" | "error";

/**
 * Sendbird can hand us HEIC/HEIF that browsers won't paint, so convert to JPEG
 * client-side. Non-HEIC urls pass straight through with no fetch.
 */
function useResolvedImageSrc(
  url: string | null,
  mimeType: string | null,
): { src: string | null; status: ResolvedImageStatus } {
  const type = mimeType?.toLowerCase() ?? "";
  const isHeic =
    url !== null &&
    (type.includes("heic") || type.includes("heif") || isHeicUrl(url));

  const [converted, setConverted] = useState<string | null>(null);
  const [status, setStatus] = useState<ResolvedImageStatus>("ready");

  useEffect(() => {
    if (url === null || !isHeic) return;
    const controller = new AbortController();
    // Owned by this effect run so it's revoked on url change or unmount; the
    // wasm decode can't be aborted, so we gate state writes on the signal.
    let objectUrl: string | null = null;
    setConverted(null);
    setStatus("converting");
    void (async () => {
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const jpeg = await convertHeicBlobToJpeg(await response.blob());
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(jpeg);
        setConverted(objectUrl);
        setStatus("ready");
      } catch {
        if (!controller.signal.aborted) setStatus("error");
      }
    })();
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, isHeic]);

  if (!isHeic) return { src: url, status: "ready" };
  return { src: converted, status };
}

function AttentionImage({
  media,
  onMediaSettled,
  onOpenMedia,
}: {
  media: AttentionImageMedia;
  onMediaSettled?: () => void;
  onOpenMedia?: (media: AttentionLightboxMedia) => void;
}) {
  const { src, status } = useResolvedImageSrc(media.url, media.mimeType);
  const boxStyle = resolveMediaBoxStyle(media, "image");

  if (status === "converting") {
    return (
      <div
        className="flex items-center justify-center overflow-hidden rounded-xl bg-surfaces-z4"
        style={boxStyle}
      >
        <Loader2 className="size-5 animate-spin text-text-tertiary" aria-hidden />
      </div>
    );
  }

  if (status === "error" || src === null) {
    return <MediaPlaceholder label="Photo unavailable" style={boxStyle} />;
  }

  return (
    // App-owned box overrides the library max-w-[370px] and reserves ratio
    // up front so load doesn't shift the thread under the reader.
    <MediaButton
      disabled={onOpenMedia === undefined}
      onOpen={() => onOpenMedia?.({ kind: "image", url: src, alt: media.alt })}
      className="block overflow-hidden rounded-xl"
      style={boxStyle}
    >
      <MessageGroup.Media.Image
        src={src}
        alt={media.alt ?? "Photo"}
        className="block size-full max-w-none object-cover"
        onLoad={onMediaSettled}
        onError={onMediaSettled}
      />
    </MediaButton>
  );
}

export function AttentionGif({
  url,
  onMediaSettled,
  onOpenMedia,
}: {
  url: string;
  onMediaSettled?: () => void;
  onOpenMedia?: (media: AttentionLightboxMedia) => void;
}) {
  return (
    <MediaButton
      disabled={onOpenMedia === undefined}
      onOpen={() => onOpenMedia?.({ kind: "image", url, alt: null })}
      className="block overflow-hidden rounded-xl"
      style={resolveMediaBoxStyle({ width: null, height: null }, "gif")}
    >
      <img
        src={url}
        alt="GIF"
        className="size-full object-cover"
        onLoad={onMediaSettled}
        onError={onMediaSettled}
      />
    </MediaButton>
  );
}

/** stopPropagation keeps the click off the surrounding reaction-picker anchor. */
export function MediaButton({
  disabled,
  onOpen,
  className,
  style,
  children,
}: {
  disabled: boolean;
  onOpen: () => void;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  if (disabled) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
      className={cn("cursor-zoom-in", className)}
      style={style}
    >
      {children}
    </button>
  );
}

/** Expand button stopPropagation so lightbox open never competes with native controls. */
export function InlineVideo({
  url,
  posterUrl,
  onMediaSettled,
  onOpen,
}: {
  url: string;
  posterUrl: string | null;
  onMediaSettled?: () => void;
  onOpen?: () => void;
}) {
  return (
    <div
      className="relative overflow-hidden rounded-xl bg-surfaces-z4"
      style={resolveMediaBoxStyle({ width: null, height: null }, "video")}
    >
      <video
        src={url}
        poster={posterUrl ?? undefined}
        controls
        playsInline
        preload="metadata"
        className="size-full object-cover"
        onLoadedMetadata={onMediaSettled}
        onLoadedData={onMediaSettled}
        onError={onMediaSettled}
      />
      {onOpen ? (
        <button
          type="button"
          aria-label="Open video"
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
          className="absolute right-2 top-2 z-10 flex size-7 items-center justify-center rounded-md bg-black/50 text-white transition-colors hover:bg-black/70"
        >
          <Maximize2 className="size-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

export function MediaPlaceholder({
  label,
  style,
}: {
  label: string;
  style?: CSSProperties;
}) {
  return (
    <span
      style={style}
      className={cn(
        "flex h-24 w-40 items-center justify-center rounded-xl bg-surfaces-z4",
        "font-body text-body-4 text-text-tertiary",
      )}
    >
      {label}
    </span>
  );
}
